import { useMemo } from "react";
import {
  useMarkNotificationSeen,
  useRedemptionNotification,
} from "../api/redemption";

export type Notification = {
  id: number;
  messageId: number;
  title: string;
  description: string;
  isSeen: number;
  createdAt: string; // dd/MM/yyyy
};

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  markAsSeen: (messageId: number) => void;
  isLoading: boolean;
  isError: boolean;
}

export const useNotifications = (): UseNotificationsReturn => {
  const { data, isLoading, isError } = useRedemptionNotification();

  const markAsSeenMutation = useMarkNotificationSeen();

  const markAsSeen = (messageId: number) => {
    markAsSeenMutation.mutate(messageId);
  };

  // Convert dd/MM/yyyy → Date
  const parseDate = (str: string) => {
    const [day, month, year] = str.split("/");
    return new Date(`${year}-${month}-${day}`);
  };

  const notifications = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];

    const unread = data.filter((n) => n.seen === 0 || n.seen === false);

    const sorted = [...unread].sort(
      (a, b) =>
        parseDate(b.createdAt).getTime() - parseDate(a.createdAt).getTime()
    );

    return sorted.slice(0, 5).map((item) => ({
      id: item.id,
      messageId: item.messageId,
      title: item.title,
      description: item.description,
      isSeen: item.seen ? 1 : 0,
      createdAt: item.createdAt,
    }));
  }, [data]);

  const unreadCount = notifications.length;

  return {
    notifications,
    unreadCount,
    markAsSeen,
    isLoading,
    isError,
  };
};
